import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const DeleteDataButton = ({ conversation: c, onDeleted, className = "" }) => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [notify, setNotify] = useState(true);
  if (user?.role !== "admin") return null;
  const run = async () => {
    try {
      const { data } = await api.post(`/conversations/${c.id}/delete-data`, { notify });
      toast.success(`Dati eliminati (${data.messages_deleted} messaggi)${data.confirmation_sent ? " · conferma inviata al cliente" : ""}`);
      qc.invalidateQueries({ queryKey: ["conversations"] });
      qc.invalidateQueries({ queryKey: ["deletion-requests"] });
      onDeleted?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <button data-testid="delete-customer-data-button" onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors ${className}`}>
        <Trash2 size={13} /> Elimina dati cliente
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="delete-data-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare tutti i dati di {c.name || c.phone}?</AlertDialogTitle>
            <AlertDialogDescription>Verranno cancellati definitivamente conversazione, messaggi, allegati, note, scheda CRM e valutazioni. L'operazione non è reversibile e viene registrata per il GDPR.</AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input data-testid="delete-data-notify-checkbox" type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="accent-[#25D366]" />
            Invia conferma al cliente su WhatsApp (se entro 24 ore)
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-data-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-data-confirm" onClick={run} className="bg-red-600 hover:bg-red-700">Elimina definitivamente</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export const DeletionBanner = ({ conversation: c, onDeleted }) => {
  if (!c.deletion_requested_at) return null;
  return (
    <div data-testid="deletion-request-banner" className="shrink-0 flex flex-wrap items-center gap-3 px-4 py-2.5 bg-red-50 border-b border-red-200 text-sm text-red-800">
      <ShieldAlert size={16} className="shrink-0" />
      <span className="flex-1 min-w-0">Il cliente ha chiesto l'<b>eliminazione dei suoi dati</b> il {new Date(c.deletion_requested_at).toLocaleString("it-IT")}.</span>
      <DeleteDataButton conversation={c} onDeleted={onDeleted} />
    </div>
  );
};
